import type { TableV2WidgetModel, TableV2WidgetRuntimeState } from '.';
import type { WidgetHeaderElement } from '../WidgetModel';
import { makeHeaderElements } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { TableV2WidgetState } from './TableV2SelfOwnedModel';
import { TableV2SelfOwnedModel, tableV2WidgetStateSchema } from './TableV2SelfOwnedModel';
import type { ReadonlySignal } from '@preact/signals-react';
import type { OutputColumnWithMetadata } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { GridRowSelectionChange } from '@thinkalpha/ui-table-svelte/gridViewProps';
import type { GridRowId } from '@thinkalpha/ui-table/types/row';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import type { TableClass, TableWidgetViewData } from '@thinkalpha/ui-types/contracts/workspace';
import { inject, injectable } from 'src/features/ioc';
import type { TableModel } from 'src/models/TableModel';
import type { ReactWidgetContainer } from 'src/ReactContainer';
import type { ProxyClientService } from 'src/services/ProxyClientService';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import { decodeSelfOwnedWidgetState } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { GetInitialData, WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { WidgetContainerController } from 'src/services/WorkspaceService/WidgetContainer/WidgetContainer';

@injectable()
export class TableV2WidgetModelImpl implements TableV2WidgetModel {
    static readonly stateSchema = tableV2WidgetStateSchema;

    static defaultState(): TableV2WidgetState {
        return { table: null, class: null, filter: null, viewData: null };
    }

    static fromJSON(container: ReactWidgetContainer): TableV2WidgetModelImpl {
        const getInitialData = container.get('GetInitialData') as GetInitialData<unknown>;
        const raw = getInitialData();
        const validated = decodeSelfOwnedWidgetState(TableV2WidgetModelImpl.stateSchema, raw);
        container.bind('InitialWidgetState').toConstantValue(validated);
        return container.get('TableV2WidgetModel') as TableV2WidgetModelImpl;
    }

    readonly #model: TableV2SelfOwnedModel;
    readonly #table: TableModel;
    readonly #disposableStack = new DisposableStack();
    readonly headerElements: WidgetHeaderElement[];

    constructor(
        @inject('WidgetId') tabId: WidgetId,
        @inject('WidgetContainerController') container: WidgetContainerController,
        @inject('InitialWidgetState') initialState: TableV2WidgetState,
        @inject('Logger') logger: Logger,
        @inject('TableModel') table: TableModel,
        @inject('ProxyClientService') proxyClientService: ProxyClientService,
        @inject('WorkspaceService') workspaceService: WorkspaceService,
    ) {
        this.#table = this.#disposableStack.use(table);
        this.#model = this.#disposableStack.use(
            new TableV2SelfOwnedModel({
                container,
                tabId,
                persisted: initialState,
                logger,
                proxyClientService,
                table: this.#table,
                workspaceService,
            }),
        );

        this.headerElements = makeHeaderElements<TableV2WidgetModel['eventBus']>([
            { type: 'localOnlyInfo', position: 21, alwaysVisible: true },
            ...COMMON_HEADER_ELEMENTS,
            { type: 'stickyFilter', position: 40, priority: 5 },
            { type: 'tableRowCount', position: 110, priority: 20 },
            { type: 'connectionStatus', position: 111, priority: 21 },
            { type: 'tableWidgetSettings', position: 112, priority: 22 },
        ]);
    }

    get type(): 'table-v2' {
        return 'table-v2';
    }

    get dirty(): ReadonlySignal<boolean> {
        return this.#model.dirty;
    }

    get columnTemplate(): ReadonlySignal<ColumnTemplate> {
        return this.#model.columnTemplate;
    }

    get classOptions(): TableV2WidgetModel['classOptions'] {
        return this.#model.classOptions;
    }

    get eventBus(): TableV2WidgetModel['eventBus'] {
        return this.#model.eventBus;
    }

    get table(): TableModel {
        return this.#table;
    }

    get filteredTableKeyOptions(): TableV2WidgetModel['filteredTableKeyOptions'] {
        return this.#model.filteredTableKeyOptions;
    }

    get resolvedColumns(): ReadonlySignal<OutputColumnWithMetadata[]> {
        return this.#model.resolvedColumns;
    }

    get tableSettingsOpen(): ReadonlySignal<boolean> {
        return this.#model.tableSettingsOpen;
    }

    get tableKeyResultSummary(): ReadonlySignal<string> {
        return this.#model.tableKeyResultSummary;
    }

    get searchQuery(): ReadonlySignal<string> {
        return this.#model.searchQuery;
    }

    get selectedTableKey(): ReadonlySignal<string | null> {
        return this.#model.selectedTableKey;
    }

    get selectedRowIds(): ReadonlySignal<ReadonlySet<GridRowId>> {
        return this.#model.selectedRowIds;
    }

    get state(): ReadonlySignal<TableV2WidgetRuntimeState> {
        return this.#model.state;
    }

    get symbolIdField(): ReadonlySignal<string | undefined> {
        return this.#model.symbolIdField;
    }

    get tableClass(): ReadonlySignal<TableClass | null> {
        return this.#model.tableClass;
    }

    get tableKeyOptions(): TableV2WidgetModel['tableKeyOptions'] {
        return this.#model.tableKeyOptions;
    }

    selectTableKey(value: string): void {
        this.#model.selectTableKey(value);
    }

    setTableSettingsOpen(open: boolean): void {
        this.#model.setTableSettingsOpen(open);
    }

    setSearchQuery(query: string): void {
        this.#model.setSearchQuery(query);
    }

    setTableClass(tableClass: TableClass | null): void {
        this.#model.setTableClass(tableClass);
    }

    updateRowSelection(change: GridRowSelectionChange): void {
        this.#model.updateRowSelection(change);
    }

    get viewData(): ReadonlySignal<TableWidgetViewData | null> {
        return this.#model.viewData;
    }

    updateColumnTemplate(columnTemplate: ColumnTemplate): void {
        this.#model.updateColumnTemplate(columnTemplate);
    }

    toJSON(): TableV2WidgetState {
        return this.#model.toJSON();
    }

    markClean(): void {
        this.#model.markClean();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
